import type { ConversationPlan, Conversation } from '@knownow/domain'
import { NotFoundError } from '@knownow/domain'
import {
  SupabaseConversationPlanRepository,
  SupabaseConversationRepository,
  CreatePlanUseCase,
  DeletePlanUseCase,
  ListPlansUseCase,
  PublishPlanUseCase,
} from '@knownow/conversation'
import { supabase } from './supabase'
import { getOrganizationBySlug } from './org-api-client'

/**
 * getById() is not organization-scoped at the query level — RLS enforces access
 * on the session-scoped Supabase client — so callers that only have a planId
 * (e.g. useRealtimePlan) use this sentinel instead of a real orgId.
 */
const RLS_SCOPED_ORG = ''

export async function listPlans(orgSlug: string): Promise<readonly ConversationPlan[]> {
  const org = await getOrganizationBySlug(orgSlug)
  const repo = new SupabaseConversationPlanRepository(supabase, org.id)
  const useCase = new ListPlansUseCase(repo)
  return useCase.execute()
}

export async function createPlan(orgSlug: string, title: string): Promise<ConversationPlan> {
  const org = await getOrganizationBySlug(orgSlug)
  const repo = new SupabaseConversationPlanRepository(supabase, org.id)
  const useCase = new CreatePlanUseCase(repo)
  return useCase.execute({ title })
}

export async function deletePlan(orgSlug: string, planId: string): Promise<void> {
  const org = await getOrganizationBySlug(orgSlug)
  const repo = new SupabaseConversationPlanRepository(supabase, org.id)
  const useCase = new DeletePlanUseCase(repo)
  await useCase.execute({ planId })
}

export async function getPlanById(planId: string): Promise<ConversationPlan | null> {
  const repo = new SupabaseConversationPlanRepository(supabase, RLS_SCOPED_ORG)
  try {
    return await repo.getById(planId)
  } catch (err) {
    if (err instanceof NotFoundError) return null
    throw err
  }
}

export interface PublishPlanResult {
  readonly plan: ConversationPlan
  readonly shareToken: string
}

export async function publishPlan(
  organizationId: string,
  planId: string,
  expectedVersion: number,
): Promise<PublishPlanResult> {
  const repo = new SupabaseConversationPlanRepository(supabase, organizationId)
  const useCase = new PublishPlanUseCase(repo)
  return useCase.execute({ planId, expectedVersion })
}

export async function listConversationsByPlan(
  organizationId: string,
  planId: string,
): Promise<readonly Conversation[]> {
  const repo = new SupabaseConversationRepository(supabase, organizationId)
  return repo.listByPlan(planId)
}
